import fr from 'flag-icons/flags/4x3/fr.svg';
import ch from 'flag-icons/flags/4x3/ch.svg';
import us from 'flag-icons/flags/4x3/us.svg';
import cr from 'flag-icons/flags/4x3/cr.svg';
import mx from 'flag-icons/flags/4x3/mx.svg';
import be from 'flag-icons/flags/4x3/be.svg';
import { codigosBanderas, contacto, destinos, fotos, lugaresViajeros, resenas, viajes } from '../data/viajes';
import { grande, img, micro, mini } from '../utils';
import { encuadreDe } from './resenas';

/** SVG de cada bandera (paquete flag-icons, servido desde el bundle). @type {Object<string, string>} */
const SVG_BANDERAS = { fr, ch, us, cr, mx, be };

/**
 * Años para mostrar: [2023, 2026] → "2023 · 2026".
 * @param {number[]} anios
 * @returns {string}
 */
export const aniosTexto = (anios) => anios.join(' · ');

/**
 * Años distintos de una lista de fotos, de menor a mayor.
 * @param {FotoWeb[]} lista
 * @returns {number[]}
 */
const aniosDe = (lista) => [...new Set(lista.map((f) => f.anio))].sort((a, b) => a - b);

/**
 * Foto del catálogo lista para la web: su año (el suyo si es de una visita posterior; si no, el de su viaje)
 * y las URLs absolutas de sus versiones web.
 * micro (200 px): pines, filas y miniaturas del lightbox · mini (560 px): polaroids y primera vista
 * del lightbox · grande (1600 px): foto completa del lightbox. La original (url) no se descarga.
 * @typedef {import('../data/viajes').Foto & { anio: number, micro: string, mini: string, grande: string }} FotoWeb
 */

/**
 * Reseña con las URLs de su foto en versión micro (avatares) y mini (polaroid), y el encuadre de sus recortes
 * (CSS object-position, siempre con valor).
 * @typedef {import('../data/viajes').Resena & { micro: string, mini: string, encuadre: string }} ResenaWeb
 */

/**
 * Viaje listo para el globo: portada en versión micro (pin y fila) y los años en que hay fotos suyas
 * (anios: [2023, 2026] · aniosTexto: "2023 · 2026").
 * @typedef {import('../data/viajes').Viaje & { anios: number[], aniosTexto: string }} ViajeWeb
 */

/**
 * Destino con los años en que hay fotos suyas (anios: [2023, 2026] · aniosTexto: "2023 · 2026").
 * @typedef {import('../data/viajes').Destino & { anios: number[], aniosTexto: string }} DestinoWeb
 */

/**
 * Cifras de "Mi mundo": de todos los viajes o solo de un año.
 * @typedef {Object} ResumenMundo
 * @property {number} destinos
 * @property {number} paises
 * @property {number} fotos
 */

/**
 * En todas las consultas, el año es opcional: sin año (null) se consideran todos.
 * @typedef {Object} DatosMundo
 * @property {number[]} anios                                   años con fotos, de menor a mayor
 * @property {function(string, ?number=): FotoWeb[]} fotosDe    fotos de un destino agrupadas por año, en orden de archivo dentro de cada año
 * @property {function(string, ?number=): FotoWeb} portadaDe    portada de un destino; con año, una foto de ese año
 * @property {function(?string, ?number=): string[]} paradasDe  paradas de un viaje en orden de ruta; con año, solo las que tienen fotos de ese año
 * @property {function(?number=): ViajeWeb[]} viajesDe          viajes; con año, solo los que tienen fotos de ese año
 * @property {function(?number=): ResumenMundo} resumen         destinos, países y fotos
 * @property {ViajeWeb[]} viajes
 * @property {ResenaWeb[]} resenas
 * @property {Object<string, DestinoWeb>} destinos
 * @property {import('../data/viajes').LugarViajeros[]} lugaresViajeros
 * @property {import('../data/viajes').Contacto} contacto
 * @property {function(string): string} bandera                 URL del SVG de la bandera de un país
 */

/**
 * Datos listos para el motor del globo: URLs absolutas desde /public (funciona aunque la app viva
 * en un subdirectorio) con el tamaño adecuado para cada uso, año de cada foto, consultas por destino,
 * viaje y año, y banderas locales.
 * @returns {DatosMundo}
 */
export function datosMundo() {
  /** Año del viaje de cada destino (el de las fotos que no traen el suyo). @type {Object<string, number>} */
  const anioDelViaje = {};
  viajes.forEach((v) => v.paradas.forEach((id) => { anioDelViaje[id] = v.anio; }));

  /** Fotos de cada destino, agrupadas por año (dentro de cada año, en orden de archivo). @type {Object<string, FotoWeb[]>} */
  const porDestino = {};
  fotos.forEach((f) => {
    const web = { ...f, anio: f.anio || anioDelViaje[f.categoria], url: img(f.url), micro: micro(f.url), mini: mini(f.url), grande: grande(f.url) };
    (porDestino[f.categoria] = porDestino[f.categoria] || []).push(web);
  });
  Object.keys(porDestino).forEach((id) => porDestino[id].sort((a, b) => a.anio - b.anio));

  /** @type {function(string, ?number=): FotoWeb[]} */
  const fotosDe = (id, anio) => (porDestino[id] || []).filter((f) => !anio || f.anio === anio);
  /** @type {function(string, ?number=): FotoWeb} */
  const portadaDe = (id, anio) => {
    const lista = fotosDe(id, anio), d = destinos[id];
    if (!lista.length) return portadaDe(id); // ese año no tiene fotos de este destino: la portada de siempre
    const n = anio && d.portadaPorAnio && d.portadaPorAnio[anio] ? d.portadaPorAnio[anio] : d.portada;
    return lista.find((f) => f.n === n) || lista[0];
  };
  /** @type {function(string[], ?number=): string[]} */
  const conFotos = (ids, anio) => ids.filter((id) => fotosDe(id, anio).length > 0);

  /** @type {Object<string, DestinoWeb>} */
  const destinosWeb = {};
  Object.keys(destinos).forEach((id) => {
    const anios = aniosDe(fotosDe(id));
    destinosWeb[id] = { ...destinos[id], anios, aniosTexto: aniosTexto(anios) };
  });

  /** @type {ViajeWeb[]} */
  const viajesWeb = viajes.map((v) => {
    const anios = aniosDe(v.paradas.flatMap((id) => fotosDe(id)));
    return { ...v, portada: micro(v.portada), anios, aniosTexto: aniosTexto(anios) };
  });

  return {
    anios: aniosDe(Object.keys(porDestino).flatMap((id) => porDestino[id])),
    fotosDe,
    portadaDe,
    paradasDe: (viajeId, anio) => {
      const viaje = viajes.find((v) => v.id === viajeId);
      return viaje ? conFotos(viaje.paradas, anio) : [];
    },
    viajesDe: (anio) => viajesWeb.filter((v) => !anio || v.anios.includes(anio)),
    resumen: (anio) => {
      const ids = conFotos(Object.keys(destinos), anio);
      return {
        destinos: ids.length,
        paises: new Set(ids.map((id) => destinos[id].pais)).size,
        fotos: ids.reduce((total, id) => total + fotosDe(id, anio).length, 0)
      };
    },
    viajes: viajesWeb,
    resenas: resenas.map((r) => ({ ...r, imagen: img(r.imagen), micro: micro(r.imagen), mini: mini(r.imagen), encuadre: encuadreDe(r) })),
    destinos: destinosWeb,
    lugaresViajeros,
    contacto,
    bandera: (pais) => SVG_BANDERAS[codigosBanderas[pais]] || ''
  };
}
